import React from "react";
import { Button } from "@/components/ui/button";
import { formatSwissCurrency } from "@/utils/format";
import type { TransactionRow } from "@/services/transactions";
import { checkBezugsteuer } from "@/services/bookingService";

interface BulkOperationsPanelProps {
  selectedIds: string[];
  transactions: TransactionRow[];
  onBulkApprove: () => Promise<void>;
  onBulkReject: () => Promise<void>;
  onBulkBook: () => Promise<void>;
  onClearSelection: () => void;
  isLoading?: boolean;
}

export const BulkOperationsPanel: React.FC<BulkOperationsPanelProps> = ({
  selectedIds,
  transactions,
  onBulkApprove,
  onBulkReject,
  onBulkBook,
  onClearSelection,
  isLoading = false,
}) => {
  const selectedTransactions = transactions.filter((t) => selectedIds.includes(t.id));

  const summary = {
    total: selectedTransactions.length,
    totalAmount: selectedTransactions.reduce((sum, t) => sum + Math.abs(t.amount), 0),
    bezugsteuer: selectedTransactions.filter((t) => checkBezugsteuer(t).required).length,
    highConfidence: selectedTransactions.filter(
      (t) => (t.ai_confidence_score || 0) >= 90
    ).length,
    approved: selectedTransactions.filter((t) => t.booking_approved).length,
    pending: selectedTransactions.filter(
      (t) => !t.booking_approved && t.review_status === "pending"
    ).length,
    alreadyBooked: selectedTransactions.filter((t) => t.journal_entry_id).length,
  };

  if (selectedIds.length === 0) {
    return null;
  }

  return (
    <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <i className="fa-solid fa-list-check text-blue-600"></i>
          Bulk-Operationen
        </h3>
        <button
          onClick={onClearSelection}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
          title="Auswahl aufheben"
        >
          <i className="fa-solid fa-times"></i>
          Auswahl aufheben
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <p className="text-xs text-gray-600 mb-1">Ausgewählt</p>
          <p className="text-2xl font-bold text-gray-900">{summary.total}</p>
        </div>
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <p className="text-xs text-gray-600 mb-1">Gesamtbetrag</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatSwissCurrency(summary.totalAmount)}
          </p>
        </div>
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <p className="text-xs text-gray-600 mb-1">Bezugsteuer</p>
          <p className="text-2xl font-bold text-orange-600">{summary.bezugsteuer}</p>
        </div>
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <p className="text-xs text-gray-600 mb-1">Hohes Vertrauen (≥90%)</p>
          <p className="text-2xl font-bold text-green-600">{summary.highConfidence}</p>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
        <div className="flex gap-6 text-sm">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">
              ⏳ Ausstehend
            </span>
            <span className="font-semibold">{summary.pending}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
              ✓ Genehmigt
            </span>
            <span className="font-semibold">{summary.approved}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
              <i className="fa-solid fa-circle-check"></i> Gebucht
            </span>
            <span className="font-semibold">{summary.alreadyBooked}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        {summary.pending > 0 && (
          <>
            <Button
              onClick={onBulkApprove}
              disabled={isLoading}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <i className="fa-solid fa-check mr-2"></i>
              {summary.pending} {summary.pending === 1 ? "Transaktion" : "Transaktionen"} genehmigen
            </Button>
            <Button
              onClick={onBulkReject}
              disabled={isLoading}
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              <i className="fa-solid fa-times mr-2"></i>
              Ablehnen
            </Button>
          </>
        )}

        {summary.approved > 0 && summary.alreadyBooked === 0 && (
          <Button
            onClick={onBulkBook}
            disabled={isLoading}
            className="bg-brand-accent hover:bg-brand-primary text-white"
          >
            <i className="fa-solid fa-book-open mr-2"></i>
            {summary.approved} {summary.approved === 1 ? "Transaktion" : "Transaktionen"} buchen
          </Button>
        )}

        {summary.alreadyBooked > 0 && (
          <div className="flex items-center gap-2 text-sm text-gray-600 px-3 py-2 bg-gray-100 rounded">
            <i className="fa-solid fa-info-circle"></i>
            {summary.alreadyBooked}{" "}
            {summary.alreadyBooked === 1 ? "Transaktion ist" : "Transaktionen sind"} bereits
            gebucht
          </div>
        )}
      </div>

      {/* Warnings */}
      {summary.bezugsteuer > 0 && (
        <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded text-sm flex items-start gap-2">
          <i className="fa-solid fa-exclamation-triangle text-orange-600 mt-0.5"></i>
          <div>
            <span className="font-semibold text-orange-800">Achtung:</span>{" "}
            <span className="text-orange-700">
              {summary.bezugsteuer}{" "}
              {summary.bezugsteuer === 1 ? "Transaktion erfordert" : "Transaktionen erfordern"}{" "}
              Bezugsteuer (Reverse Charge)
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
